#import "src/fragments/workspace.graphql"

fragment WorkspaceParts on Workspace {
  id
  name
  description
  created
  isActive
  config {
    allocation {
      ...AllocationConfigFragment
    }
  }
}

fragment TagConfigFragment on TagConfig {
  id
  name
  allowPersonToHaveTag
  allowGroupToHaveTag
  allowPositionToHaveTag
  allowMultipleOnEntity
  allowNewTags
  allowedGroupTypes {
    key
    label: key
    value: key
  }
  visibleIn
  isReserved
  isReadonly
  details
  isEditableByUsers
  isGroupBadge
  isGroupAssociation
  attributes
  showPreviousAppliedTags
  allowedToEditTagDisplayValue
  isPersonBadge
  readOnly
  allowEditingReadOnlyTagsByAdmin
  allowEditingReadOnlyTagsByPowerUser
  hideShowInfoIconInTagDisplay
  hideTagTypeInTagDisplay
  showInExplore
  isActive
  description
}

fragment TagValueFragment on Tag {
  id
  type
  isActive
  label: displayValue
  value: id
  attributes
  appliedCount
  __typename
}

mutation createWorkspace($input: CreateWorkspaceInput!) {
  workspace: createWorkspace(input: $input) {
    ...FullWorkspaceFragment
  }
}

mutation markWorkspaceAsActive($id: ID!) {
  workspace: markWorkspaceAsActive(id: $id) {
    ...FullWorkspaceFragment
  }
}

mutation deleteWorkspace($id: ID!) {
  deleteWorkspace(id: $id)
}

mutation cloneWorkspace(
  $sourceWorkspaceId: ID!
  $clonedWorkspaceName: String!
  $preferredUsername: String!
  $skeletonOnly: Boolean
  $excludeHistory: Boolean
  $historyCutOffDate: Date
  $useBtreeIndex: Boolean = false
) {
  cloneWorkspace(
    sourceWorkspaceId: $sourceWorkspaceId
    clonedWorkspaceName: $clonedWorkspaceName
    preferredUsername: $preferredUsername
    skeletonOnly: $skeletonOnly
    excludeHistory: $excludeHistory
    historyCutOffDate: $historyCutOffDate
    useBtreeIndex: $useBtreeIndex
  ) {
    id
    jobId
    jobName
  }
}

mutation updateWorkspaceStrategyConfig($input: StrategyConfigInput!) {
  workspace: updateWorkspaceStrategyConfig(input: $input) {
    ...FullWorkspaceFragment
  }
}

query getWorkspaces {
  workspaces: getWorkspaces {
    ...PartialWorkspaceFragment
  }
}

query getWorkspacesForManagement {
  workspaces: getWorkspaces {
    ...ManagementWorkspaceFragment
  }
}

query getGroupAccessListCsvExport {
  csvExport: getGroupAccessListCsvExport {
    content
  }
}

query listWorkspacePrincipleSets {
  principleSets: listPrincipleSets(includeExperimental: true) {
    id
    name
    suitableGroupTypes
    definitions {
      type
      args
    }
  }
}

query getWorkspaceConfigTypes($target: String = "") {
  types: getWorkspaceConfigTypes(target: $target) {
    id
    displayName
    description
  }
}

mutation updateWorkspaceGroupTypes($input: UpdateGroupTypes!) {
  workspace: updateWorkspaceGroupTypes(input: $input) {
    ...FullWorkspaceFragment
  }
}

mutation updateWorkspaceEmailNotificationConfig(
  $input: UpdateEmailNotificationConfig!
) {
  workspaceConfig: updateWorkspaceEmailNotificationConfig(input: $input) {
    ...FullWorkspaceFragment
  }
}

mutation updateWorkspaceAllocationConfig($input: UpdateAllocationConfig!) {
  workspace: updateWorkspaceAllocationConfig(input: $input) {
    id
    config {
      allocation {
        ...AllocationConfigFragment
      }
    }
  }
}

mutation updateWorkspaceNotifications($input: UpdateNotifications!) {
  workspace: updateWorkspaceNotifications(input: $input) {
    ...FullWorkspaceFragment
  }
}

mutation updateWorkspacePrincipleSets($input: UpdatePrincipleSets!) {
  workspace: updateWorkspacePrincipleSets(input: $input) {
    ...FullWorkspaceFragment
  }
}

mutation setWorkspaceReport($id: ID!, $enableReport: Boolean!) {
  result: setWorkspaceReport(id: $id, enableReport: $enableReport) {
    id
    isReportingEnabled
  }
}

mutation submitDataExtractBatchJob(
  $workspaceId: ID!
  $force: Boolean
  $logLevel: String
  $disableCdk: Boolean
  $disableDataUpload: Boolean
  $disableExtract: Boolean
  $disableTransform: Boolean
  $disableComparison: Boolean
  $disableEmptyFileCheck: Boolean
  $disableCleanup: Boolean
  $disableValidation: Boolean
  $canary: Boolean
) {
  result: submitDataExtractBatchJob(
    workspaceId: $workspaceId
    force: $force
    logLevel: $logLevel
    disableCdk: $disableCdk
    disableDataUpload: $disableDataUpload
    disableExtract: $disableExtract
    disableTransform: $disableTransform
    disableComparison: $disableComparison
    disableEmptyFileCheck: $disableEmptyFileCheck
    disableCleanup: $disableCleanup
    disableValidation: $disableValidation
    canary: $canary
  )
}

# Tags Config Related queries and mutation
query getWorkspaceTagTypesList($includeInactive: Boolean) {
  tagTypes: getTagConfigList(input: { includeInactive: $includeInactive }) {
    ...TagConfigFragment
  }
}

query getTagConfigById($tagConfigId: String) {
  result: getTagConfigById(tagConfigId: $tagConfigId) {
    ...TagConfigFragment
  }
}

query getWorkspaceTagsById($tagConfigId: String, $tagTypes: [TagType]) {
  tagConfig: getTagConfigById(tagConfigId: $tagConfigId) {
    ...TagConfigFragment
  }
  tagValues: searchTags(tagTypes: $tagTypes, includeInactiveTags: true) {
    ...TagValueFragment
  }
}

query getWorkspaceTagTypeById($tagConfigId: String) {
  tagConfig: getTagConfigById(tagConfigId: $tagConfigId) {
    ...TagConfigFragment
  }
}

query pagedSearchTags($input: PagedSearchTagsInput) {
  results: pagedSearchTags(input: $input) {
    tags {
      ...TagValueFragment
    }
    totalCount
  }
}

# Query to fetch extended help config - only use in settings pages
# This query will fail if backend doesn't support these fields yet
query getWorkspaceHelpConfig($id: ID!) {
  workspace: getWorkspace(id: $id) {
    ...WorkspaceHelpConfigFragment
  }
}

mutation updateWorkspaceConfig($input: UpdateWorkspaceConfigInput!) {
  workspace: updateWorkspaceConfig(input: $input) {
    id
    name
    description
    config {
      fteConfig {
        decimal
        currencySymbol
      }
      positionHeadcount {
        wholeNumbersOnly
      }
      locale
      helpConfig {
        customHelpMenuTitle
        customHelpMenuDescription
        showTeamFormSupportRequest
        showTeamFormHelpContent
        showCustomHelpContent
        externalContents {
          title
          description
          href
          type
          iconUrl
          locations
          roles
        }
      }
    }
  }
}

mutation updateWorkspaceTagConfig($input: UpdateTagConfigInput) {
  tagConfig: updateTagConfigById(input: $input) {
    id
  }
}

mutation updateAllWorkspaceTagConfigs($input: [UpdateTagConfigInput]) {
  tagConfig: updateAllTagConfigs(input: $input) {
    id
  }
}

mutation updateTagValue($input: UpdateTagInput!) {
  tag: updateTag(input: $input) {
    ...TagValueFragment
  }
}

mutation addNewTag($input: AddNewTagInput!) {
  result: addNewTag(input: $input) {
    ...TagValueFragment
  }
}

query getUsersWithWorkspaceAccess($input: UsersWithWorkspaceAccessInput!) {
  result: getUsersWithWorkspaceAccess(input: $input) {
    users {
      email
      name
      status
    }
    next
  }
}

mutation removeWorkspaceAccessForUser($input: RemoveWorkspaceAccessInput!) {
  result: removeWorkspaceAccessForUser(input: $input) {
    email
  }
}

mutation runEmailNotificationJob($sourceWorkspaceId: ID!) {
  runEmailNotificationJob(sourceWorkspaceId: $sourceWorkspaceId) {
    id
    jobId
    jobName
  }
}

query searchReportingJobs($input: SearchReportingJobsInput) {
  result: searchReportingJobs(input: $input) {
    jobs {
      workspaceId
      jobId
      jobName
      status
      startTime
      endTime
      duration
      logStreamLink
      jobLink
      metricsLink
      vcpu
      memory
      executionTimeout
      force
      canary
      logLevel
      nodeAllocatedMemory
      disableCdk
      disableDataUpload
      disableExtract
      disableTransform
      disableComparison
      disableEmptyFileCheck
      disableCleanup
      disableValidation
    }
    nextToken
  }
}
